import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService from '../../services/caregiverService';
import AuthShell, { AuthNotice } from '../Auth/AuthShell';
import { validateCaregiverPin, PIN_REQUIREMENTS_HINT } from './pinValidation';

/**
 * Caregiver invite acceptance page (FECOA-1320 email-invitation flow).
 *
 * Read the single-use token from the URL (?token=...), let the caregiver
 * set their initial PIN, and route into the dashboard.
 */
const CaregiverInviteAccept = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  useEffect(() => {
    const t = searchParams.get('token') || '';
    setToken(t);
    if (!t) {
      setError('No invitation token found in the URL. Please use the link from your email.');
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) return;
    const policyError = validateCaregiverPin(newPin);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPin !== confirmPin) {
      setError('PINs do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await caregiverService.acceptInvite({ token, new_pin: newPin });
      toast.success('Welcome — your account is ready.');
      navigate('/caregiver/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail;
      setError(detail || 'Could not accept invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'input h-11 pl-10 pr-10 text-[15px] tracking-wide';

  return (
    <AuthShell
      title="Set Up Your Account"
      subtitle="Choose a PIN to use whenever you sign in."
    >
      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="label">New PIN</label>
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type={showNewPin ? 'text' : 'password'}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              autoComplete="new-password"
              inputMode="numeric"
              className={inputCls}
            />
            <button
              type="button"
              onClick={() => setShowNewPin((s) => !s)}
              aria-label={showNewPin ? 'Hide PIN' : 'Show PIN'}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              {showNewPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="hint">{PIN_REQUIREMENTS_HINT}</p>
        </div>

        <div>
          <label className="label">Confirm PIN</label>
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type={showConfirmPin ? 'text' : 'password'}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              autoComplete="new-password"
              inputMode="numeric"
              className={inputCls}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPin((s) => !s)}
              aria-label={showConfirmPin ? 'Hide PIN' : 'Show PIN'}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              {showConfirmPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && <AuthNotice tone="danger">{error}</AuthNotice>}

        <button
          type="submit"
          disabled={submitting || !token}
          className="btn btn-primary btn-lg w-full"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate Account'}
        </button>
      </form>
    </AuthShell>
  );
};

export default CaregiverInviteAccept;
